<template>
  <div>
    <export-dialog
      v-if="exportDialog"
      filename="Épület napló"
      api-endpoint="/log/exportBuilding"
      @close="exportDialog = false"
      :additional-post-params="{ period: search.period, from: search.from, to: search.to }"
    />

    <title-row
      title="Épület napló"
      :subtitle="dataTable.itemsLength === -1 ? 'Betöltés...' : `${dataTable.itemsLength} log van a rendszerben`"
      :breadcrumb-items="[
        {
          text: 'Naplók',
          disabled: true,
        },
        {
          text: 'Épület',
          disabled: true,
        },
      ]"
    >
      <template #buttons>
        <title-row-button @click="togglePolling" :color="polling ? 'primary' : 'muted'">
          <v-icon v-if="!loading" left>{{ polling ? 'mdi-stop' : 'mdi-play' }}</v-icon>
          <v-progress-circular
            v-else
            indeterminate
            size="18"
            width="2"
            class="mr-2 ml-n1" />
          Frissítés
        </title-row-button>

        <title-row-button @click="exportDialog = true" color="muted">
          <v-icon left>mdi-file-export</v-icon>
          Exportálás
        </title-row-button>
      </template>
    </title-row>

    <v-expansion-panels class="mb-4 rounded-xl overflow-hidden" :mandatory="!!Object.values(search).find((e) => e !== '')" flat>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span>
            <v-icon left>mdi-filter</v-icon>
            Szűrés
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row class="mt-2">
            <v-col :cols="12" :md="3" :lg="3">
              <v-autocomplete
                label="Épület"
                :items="buildings"
                item-text="name"
                item-value="id"
                dense
                clearable
                hide-details
                v-model="search.building_ids"
                placeholder="Összes"
                multiple
                @change="searchChangeEventHandler"
                filled
              />
            </v-col>
            <v-col :cols="12" :md="3" :lg="3">
              <v-autocomplete
                label="Kontroller"
                :items="controllers"
                item-text="name"
                item-value="element_id"
                dense
                clearable
                hide-details
                v-model="search.controller_ids"
                placeholder="Összes"
                multiple
                @change="searchChangeEventHandler"
                filled
              />
            </v-col>
            <!-- <v-col :cols="12" :md="3" :lg="2">
              <v-autocomplete
                label="Típus"
                :items="logTypes"
                item-text="label"
                item-value="value"
                dense
                hide-details
                clearable
                v-model="search.log_type"
                placeholder="Összes"
                @change="searchChangeEventHandler"
              />
            </v-col> -->
            <v-col :cols="12" :md="3" :lg="3">
              <v-text-field
                label="Név / Kártyaszám / Vonalkód száma"
                dense
                clearable
                hide-details
                v-model="search.keyphrase"
                @input="searchChangeEventHandler"
                filled
              />
            </v-col>
            <v-col :cols="12" :md="3" :lg="3">
              <v-select
                label="Időkorlát"
                :items="timePeriods"
                item-text="label"
                item-value="value"
                dense
                hide-details
                v-model="search.period"
                @change="searchChangeEventHandler"
                filled
              />
            </v-col>
            <template v-if="search.period === 'custom'">
              <v-col :cols="12" :md="3" :lg="2">
                <v-datetime-picker
                  label="Időkorlát kezdete"
                  v-model="search.from"
                  clearable
                  dense
                  :date-picker-props="{ allowedDates: allowedStartDates }"
                  :time-picker="false"
                  @change="searchChangeEventHandler"
                  :error-messages="search.from ? '' : 'Kötelező!'"
                  filled
                />
              </v-col>
              <v-col :cols="12" :md="3" :lg="2">
                <v-datetime-picker
                  label="Időkorlát vége"
                  v-model="search.to"
                  clearable
                  dense
                  :date-picker-props="{ allowedDates: allowedEndDates }"
                  :time-picker="false"
                  :disabled="!search.from"
                  @change="searchChangeEventHandler"
                  :error-messages="search.from && !search.to ? 'Kötelező!' : ''"
                  filled
                />
              </v-col>
            </template>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card class="rounded-xl" flat>
      <v-data-table
        :loading="dataTable.loading"
        :headers="dataTable.headers"
        :items="dataTable.items"
        :server-items-length="dataTable.itemsLength"
        :options.sync="dataTable.options"
        :footer-props="$config.dataTableFooterProps"
        calculate-widths
        disable-sort
      >
        <template #[`item.created`]="{ item }">
          <span :title="item.created | moment($config.momentFormats.explicit)">
            {{ item.created | moment('ll LTS') }}
          </span>
        </template>
        <template #[`item.cardowner_name`]="{ item }">
          <div class="font-weight-bold d-flex align-center gap-x-1">
            <span>{{ item.cardowner_name }}</span>
            <v-btn
              v-if="item.element_id && hasRight('CARD_READ')"
              x-small
              icon
              color="primary"
              class="action-btn"
              @click.stop="openNewtab(item.element_id, 'CardEditor')"
            >
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
          <div>
            {{ item.card_rule_prefix + item.card_number || item.barcode_number }}
          </div>
        </template>
        <template #[`item.controller_name`]="{ item }">
          <div class="d-flex align-center gap-x-1">
            <span>{{ item.controller_name }}</span>
            <v-btn
              v-if="item.element_id && hasRight('DEVICE_READ')"
              x-small
              icon
              color="primary"
              class="action-btn"
              @click.stop="openNewtab(item.controller_id, 'ControllerEditor')"
            >
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
        </template>
        <template #[`item.message`]="{ item }">
          <div class="px-4">{{ item.message }}</div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { dataTablePage } from '@/mixins';
import ExportDialog from '@/components/ExportDialog.vue';

export default {
  components: { ExportDialog },
  mixins: [dataTablePage],

  data() {
    return {
      exportDialog: false,
      x: 0,
      y: 0,
      polling: false,
      buildingType: 'P',
      waiting: false,
      routeName: 'BuildingLog',
      fetchItems: this.fetchBuildingLog,
      dataTable: {
        headers: [
          { text: '#', value: 'logid' },
          { text: 'Idő', value: 'created' },
          { text: 'Név', value: 'cardowner_name', width: 360},
          { text: 'Kapu', value: 'gate_name' },
          { text: 'Kontroller', value: 'controller_name', sortable: false, width: 200 },
          { text: 'Típus', value: 'event', sortable: false },
          { text: 'Információ', value: 'message', sortable: false},
        ],
      },
      timePeriods: [
        {
          label: 'Utolsó 24 óra',
          value: '1 days',
        },
        {
          label: 'Utolsó hét',
          value: '1 weeks',
        },
        {
          label: 'Utolsó hónap',
          value: '1 months',
        },
        {
          label: 'Egyéni',
          value: 'custom',
        },
      ],
      search: {
        keyphrase: '',
        log_type: 'enter',
        building_ids: [],
        building_type: 'B',
        period: '1 days',
        controller_ids: [],
        from: '',
        to: '',
      },
      logs: [],
      pollingInterval: null,
      pollingTimeout: null,
      pollingPausedByPagination: false,
      firstFetch: true,
    };
  },

  created() {
    if (this.options.page == 1) {
      this.polling = true;
    }
  },

  beforeDestroy() {
    this.polling = false;
    clearTimeout(this.pollingTimeout);
  },

  watch: {
    'dataTable.options': {
      handler(val) {
        if (val.page > 1) {
          if (this.polling) {
            clearTimeout(this.pollingTimeout);
            this.polling = false;
            this.pollingPausedByPagination = true;
          }
        } else if (this.pollingPausedByPagination) {
          this.fetchBuildingLog(null, true);
          this.polling = true;
          this.pollingPausedByPagination = false;
        }
      },
      deep: true,
    },

    search: {
      handler() {
        this.dataTable.loading = true;
      },
      deep: true,
    },
  },

  methods: {
    parseSearchQueries() {
      return {
        keyphrase: this.$route.query.keyphrase,
        log_type: this.$route.query.log_type,
        period: this.$route.query.period,
        controller_ids: this.$route.query.controller_ids
          ? this.toArray(this.$route.query.controller_ids)
          : [],
        building_ids: this.$route.query.building_ids
          ? this.toArray(this.$route.query.building_ids)
          : [],
        from: this.$route.query.from,
        to: this.$route.query.to,
      };
    },

    openNewtab(controllerId, controllerName) {
      const routeData = this.$router.resolve({
        name: controllerName,
        params: { id: controllerId }
      });
      window.open(routeData.href, '_blank');
    },

    toArray(param) {
      return Array.isArray(param) ? param : [param];
    },

    togglePolling() {
      this.polling = !this.polling;
      if (this.polling) {
        this.fetchItems(null, true);
      } else {
        clearTimeout(this.pollingTimeout);
      }
    },

    allowedStartDates(val) {
      const now = new Date();
      const startDate = new Date(val);
      if (this.search.to?.length) {
        let intervalStart = new Date(this.search.to);
        intervalStart.setMonth(intervalStart.getMonth() - 2);
        return (
          startDate.getTime() <= now.getTime() && startDate.getTime() >= intervalStart.getTime()
        );
      }
      return startDate.getTime() <= now.getTime();
    },

    allowedEndDates(val) {
      const now = new Date();
      const startDate = new Date(this.search.from);
      const endDate = new Date(val);
      let intervalEnd = new Date(this.search.from);
      intervalEnd.setMonth(intervalEnd.getMonth() + 2);
      intervalEnd = Math.min(intervalEnd.getTime(), now.getTime());
      return endDate.getTime() >= startDate.getTime() && endDate.getTime() <= intervalEnd;
    },

    searchChangeEventHandler(val) {
      if (
        this.search.period === 'custom' &&
        (!this.search.from?.length || !this.search.to?.length)
      ) {
        return;
      }
      this.searchEventHandler();
    },

    async fetchBuildingLog(origin = null, keepPolling = false) {
      if (origin === 'searchEventHandler' || this.firstFetch) {
        this.dataTable.loading = true;
      }

      if (this.firstFetch) {
        keepPolling = this.polling;
        this.firstFetch = false;
      }

      this.loading = true;
      try {
        this.abortController = new AbortController();
        const response = await this.$http.post('logs/list/building', this.postData, {
          signal: this.abortController.signal,
        });
        if (this.polling && keepPolling) {
          this.pollingTimeout = setTimeout(() => {
            this.fetchBuildingLog(null, true);
          }, 2000);
        }
        if (response) {
          this.dataTable.items = response.logs;
          this.dataTable.itemsLength = response.logs_count;
          this.dataTable.options.page = this.$route.query.page * 1 || 1;
        }
      } catch (e) {
        this.polling = false;
        console.error(e);
      } finally {
        this.loading = false;
        this.dataTable.loading = false;
        this.abortController = null;
      }
    },
  },

  computed: {
    searchQueries() {
      return {
        keyphrase: this.search.keyphrase || '',
        period: this.search.period || '',
        log_type: this.search.log_type || '',
        building_ids: this.search.building_ids.map((e) => Number(e)) || [],
        controller_ids: this.search.controller_ids.map((e) => Number(e)) || [],
        building_type: this.search.building_type || '',
        from: this.search.from || '',
        to: this.search.to || '',
      };
    },
  },

  asyncComputed: {
    buildings: {
      async get() {
        const response = await this.$http.post('buildings/list', {
          type: 'B', // Épületek
        });
        return response.buildings;
      },
      default: [],
    },
    controllers: {
      async get() {
        const response = await this.$http.post('controllers/list', {
          type: 'B', // Épületek
        });
        return response.controllers;
      },
      default: [],
    },
    logTypes: {
      async get() {
        const response = await this.$http.post('daemon-logs/types/list');
        return response.log_types;
      },
      default: [],
    },
  },
};
</script>

<style>
.v-data-table .action-btn {
  display: none;
}

.v-data-table tbody tr:hover .action-btn {
  display: inline-flex;
}
</style>

